import React, { useState } from 'react';
import { DillButton, DillModal } from '../../../general/Dill';
import { openDillToast } from '../../../../utils/helpers';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { sendInternalSignatureDocumentsForSignature } from '../../../../redux/services/internalSignatureDocumentService';
import { PaperPlaneTilt } from 'phosphor-react';

// Manual "Send for Signature" trigger, mirroring ShareApplicationModal's shape — this never
// shows to the applicant, it's a supplier-only action inside CreditApplicationDetails.
const SendInternalSignatureDocumentsModal = ({
  open,
  handleClose,
  creditApplicationId,
  onSuccess,
}: {
  open: boolean;
  handleClose: () => void;
  creditApplicationId: string;
  onSuccess: () => void;
}) => {
  const dispatch = useAppDispatch();
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const [loading, setLoading] = useState(false);

  const handleSend = async () => {
    setLoading(true);
    try {
      const result = await dispatch(
        sendInternalSignatureDocumentsForSignature({
          supplierId: currentSupplier?.id ?? '',
          creditApplicationId,
        })
      );
      if (result.type.includes('fulfilled')) {
        openDillToast({
          type: 'SUCCESS',
          message: 'Internal signature documents sent for signature.',
        });
        onSuccess();
        handleClose();
      } else {
        openDillToast({
          type: 'ERROR',
          message:
            (result.payload as string) ||
            'Failed to send internal signature documents — check that the credit application template has internal signature documents configured.',
        });
      }
    } catch (error: any) {
      openDillToast({
        type: 'ERROR',
        message: error?.message || 'Failed to send internal signature documents.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <DillModal
      open={open}
      onClose={handleClose}
      title="Send Internal Signature Documents for Signature"
      subtitle="Sends (or resends) this application's internal signature documents to whoever is eligible to sign, based on the assigned branch. The applicant never sees this."
      footer={
        <div className="flex justify-end w-full gap-2">
          <DillButton label="Close" variant="secondary" onClick={handleClose} />
          <DillButton
            label="Send"
            onClick={handleSend}
            isLoading={loading}
            isDisabled={loading}
            icon={<PaperPlaneTilt size={16} />}
          />
        </div>
      }>
      <div className="text-sm text-gray-600">
        This will (re)send a sign request to whichever internal signer(s) this application still
        needs, based on the requested credit amount and the assigned branch/region.
      </div>
    </DillModal>
  );
};

export default SendInternalSignatureDocumentsModal;
